import React from 'react';
import { Notice, Switch, TextInput } from '@origo/origo-ui';
import type { PreflightReport, PreflightRequest, SyncDirectionPolicy } from '../../../api/types';
import { BINDING_ID_ERROR, BINDING_ID_PATTERN } from '../bindingId';
import { DirectionPolicyField } from '../DirectionPolicyField';
import { type LayerDraft, isLayerAssigned, resolveLokalIdField } from './draft';
import styles from './create.module.css';

export interface CreateMetadata {
    bindingId: string;
    displayName: string;
    enabled: boolean;
    /** null til brukeren har valgt — opprettelse er blokkert inntil da. */
    directionPolicy: SyncDirectionPolicy | null;
}

interface CreateConfirmStepProps {
    metadata: CreateMetadata;
    onMetaChange: (patch: Partial<CreateMetadata>) => void;
    request: PreflightRequest;
    layers: LayerDraft[];
    /** Preflight-rapporten mappingen er bygd fra — kilden til «må rettes»-listen. */
    report: PreflightReport | null;
    error: string | null;
}

/** Steg 3: bindings-metadata, gjenstående preflight-krav, og oppsummering før lagring. */
export const CreateConfirmStep: React.FC<CreateConfirmStepProps> = ({
    metadata,
    onMetaChange,
    request,
    layers,
    report,
    error,
}) => {
    const trimmedId = metadata.bindingId.trim();
    const idError =
        trimmedId && !BINDING_ID_PATTERN.test(trimmedId) ? BINDING_ID_ERROR : undefined;
    const assignedLayers = layers.filter(isLayerAssigned);
    const mappedFields = assignedLayers.reduce(
        (sum, l) => sum + l.fields.filter((f) => f.agolFieldName.trim()).length,
        0,
    );
    const editorField = report?.layers.map((l) => l.editorField).find(Boolean) ?? null;
    const editDateField = report?.layers.map((l) => l.editDateField).find(Boolean) ?? null;
    const requiredActions = report?.requiredActions ?? [];

    return (
        <div className={styles.single}>
            {error && <Notice variant="danger">{error}</Notice>}

            {requiredActions.length > 0 && (
                <Notice variant="warning">
                    <p className={styles.noticeHeading}>Tjenesten er ikke helt klar for synk</p>
                    <p>
                        Koblingen kan opprettes, men synkroniseringen vil ikke oppføre seg riktig
                        før dette er rettet på AGOL-tjenesten:
                    </p>
                    <ul className={styles.actionList}>
                        {requiredActions.map((action) => (
                            <li key={action}>{action}</li>
                        ))}
                    </ul>
                </Notice>
            )}

            <div className={styles.fieldGroup}>
                <p className={styles.sectionHeading}>Kobling</p>
                <TextInput
                    label="Kobling-ID"
                    placeholder="f.eks. bygg-oslo"
                    value={metadata.bindingId}
                    error={idError}
                    helperText="1–63 tegn: små bokstaver, tall og bindestrek. Kan ikke endres senere."
                    onChange={(e) => onMetaChange({ bindingId: e.target.value })}
                />
                <TextInput
                    label="Visningsnavn"
                    placeholder="Bygg – Oslo kommune"
                    value={metadata.displayName}
                    onChange={(e) => onMetaChange({ displayName: e.target.value })}
                />
                <Switch
                    label="Aktivert"
                    checked={metadata.enabled}
                    onChange={(c) => onMetaChange({ enabled: c })}
                />
                <DirectionPolicyField
                    name="create-direction-policy"
                    value={metadata.directionPolicy}
                    onChange={(p) => onMetaChange({ directionPolicy: p })}
                />
            </div>

            <div className={styles.fieldGroup}>
                <p className={styles.sectionHeading}>Oppsummering</p>
                <div className={styles.summaryGrid}>
                    <span className={styles.summaryLabel}>Forvaltningssystem</span>
                    <span>{request.forvaltningssystem}</span>
                    <span className={styles.summaryLabel}>Sentralt datasett</span>
                    <span className={styles.mono}>{request.centralDatasetId}</span>
                    <span className={styles.summaryLabel}>AGOL-URL</span>
                    <span className={styles.breakAll}>{request.agolBaseUrl}</span>
                    <span className={styles.summaryLabel}>Koblede objekttyper</span>
                    <span>
                        {assignedLayers.length} av {layers.length}
                    </span>
                    <span className={styles.summaryLabel}>Mappede felt</span>
                    <span>{mappedFields}</span>
                    <span className={styles.summaryLabel}>LokalId-felt</span>
                    <span className={styles.mono}>{resolveLokalIdField(layers)}</span>
                    <span className={styles.summaryLabel}>Editor-tracking</span>
                    <span className={styles.mono}>
                        {editorField && editDateField
                            ? `${editorField} / ${editDateField}`
                            : 'mangler'}
                    </span>
                </div>
                <p className={styles.intro}>
                    Companion-tabellen for synk-metadata opprettes automatisk i AGOL når koblingen
                    lagres.
                </p>
            </div>
        </div>
    );
};
